import { create } from 'zustand'
import { calculateSdaIncomeAndNoi } from '../utils/sdaFormulaCalculator'
import { getSavedRenegotiatedPrice } from '../utils/renegotiationValidation'

// ─── Scenario Datasets ─────────────────────────────────────────────────────
const SCENARIOS = {
  base: {
    label: 'Base Case',
    color: '#1d4ed8',
    // Income
    grossRentalIncome: 1_320_000,
    otherIncome: 48_000,
    vacancyRate: 0.07,          // 7%
    // Expenses
    propertyTax: 112_000,
    insurance: 38_000,
    utilities: 42_000,
    maintenance: 56_000,
    management: 0,              // calculated as % of EGI
    managementPct: 0.06,
    reserves: 28_000,
    // Financing
    purchasePrice: 14_500_000,
    loanAmount: 10_150_000,     // 70% LTV
    interestRate: 0.065,
    amortization: 30,
    // Hold
    holdYears: 5,
    exitCapRate: 0.055,
    annualRentGrowth: 0.03,
    annualExpenseGrowth: 0.025,
  },
  best: {
    label: 'Best Case',
    color: '#1d4ed8',
    grossRentalIncome: 1_440_000,
    otherIncome: 60_000,
    vacancyRate: 0.05,
    propertyTax: 112_000,
    insurance: 36_000,
    utilities: 38_000,
    maintenance: 48_000,
    management: 0,
    managementPct: 0.05,
    reserves: 24_000,
    purchasePrice: 14_500_000,
    loanAmount: 10_150_000,
    interestRate: 0.060,
    amortization: 30,
    holdYears: 5,
    exitCapRate: 0.050,
    annualRentGrowth: 0.04,
    annualExpenseGrowth: 0.02,
  },
  worst: {
    label: 'Worst Case',
    color: '#1e3a8a',
    grossRentalIncome: 1_200_000,
    otherIncome: 36_000,
    vacancyRate: 0.12,
    propertyTax: 118_000,
    insurance: 44_000,
    utilities: 48_000,
    maintenance: 68_000,
    management: 0,
    managementPct: 0.07,
    reserves: 36_000,
    purchasePrice: 14_500_000,
    loanAmount: 10_150_000,
    interestRate: 0.072,
    amortization: 30,
    holdYears: 5,
    exitCapRate: 0.065,
    annualRentGrowth: 0.015,
    annualExpenseGrowth: 0.035,
  },
}

const toFiniteNumber = (value) => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : 0
}

const positiveNumber = (value) => {
  const numeric = toFiniteNumber(value)
  return numeric > 0 ? numeric : 0
}

const excelMaxZero = (value) => Math.max(0, toFiniteNumber(value))
const hasNumericValue = (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))

function buildScenariosFromDealState(dealState) {
  const financials = dealState?.extractedFinancials || {}
  const rawDeal = dealState?.rawDealData || {}
  const revenue = financials.revenue || {}
  const expenses = financials.expenses || {}

  const rawPrice = positiveNumber(rawDeal.price)
  const savedRenegotiatedPrice = getSavedRenegotiatedPrice(dealState?.renegotiation)
  const unitCount = positiveNumber(rawDeal.property?.units) || 1
  // Guard against OM parser extracting a per-unit figure or page number as the
  // asking price.  For multifamily, anything below $20K/unit is clearly wrong.
  const pricePerUnit = rawPrice > 0 ? rawPrice / unitCount : 0
  const validRawPrice = rawPrice > 0 && pricePerUnit >= 20_000
  const storedNOI = toFiniteNumber(financials.noi)
  // When no valid price is in the OM (e.g. "Request for Offer"), derive an
  // implied purchase price from the document NOI at a 6.5 % cap rate rather
  // than leaving it as 0 (which makes Cap Rate / DSCR / IRR show as 0).
  const IMPLIED_CAP_RATE = 0.065
  const purchasePrice = savedRenegotiatedPrice > 0
    ? savedRenegotiatedPrice
    : validRawPrice
      ? rawPrice
    : storedNOI > 0
      ? Math.round(storedNOI / IMPLIED_CAP_RATE)
      : 0
  const otherIncome = hasNumericValue(revenue.otherIncome)
    ? excelMaxZero(revenue.otherIncome)
    : SCENARIOS.base.otherIncome
  const totalRevenue = positiveNumber(revenue.totalRevenue)
  const grossRentalIncome = positiveNumber(revenue.grossPotentialRent)
    || Math.max(totalRevenue - otherIncome, 0)
    || SCENARIOS.base.grossRentalIncome
  const occupancyRate = positiveNumber(revenue.rentRoll?.occupancyRate || financials.occupancy) || 93
  // Use explicitly-stored vacancyRate if available; fall back to deriving from occupancy.
  // Clamp raised to 0.30 to handle high-vacancy value-add deals (previously 0.18 was too low).
  const storedVacancyRate = hasNumericValue(revenue.vacancyRate)
    ? Math.abs(Number(revenue.vacancyRate)) >= 1
      ? Math.abs(Number(revenue.vacancyRate)) / 100
      : Math.abs(Number(revenue.vacancyRate))
    : null
  const vacancyRate = storedVacancyRate !== null
    ? Math.max(0.02, Math.min(0.30, storedVacancyRate))
    : Math.max(0.02, Math.min(0.30, (100 - occupancyRate) / 100))
  const income = calculateSdaIncomeAndNoi({
    grossPotentialRentAnnual: grossRentalIncome,
    otherIncomeAnnual: otherIncome,
    vacancyRate,
    operatingExpensesAnnual: positiveNumber(expenses.totalOperatingExpenses || expenses.totalExpenses),
    parsedNoi: storedNOI,
    // Trust the NOI that Module 3 saved — it is the document-extracted actual
    // (T12 > OM pro-forma > formula).  Only skip when there is nothing stored.
    trustParsedNoi: storedNOI > 0,
  })
  const effectiveGrossIncome = income.effectiveGrossIncome
  const extractedNOI = storedNOI  // already computed above
  const extractedExpenses = positiveNumber(expenses.totalOperatingExpenses || expenses.totalExpenses)
  // Always trust the extracted NOI when available — the OM may include reserves or
  // other deductions outside of "operating expenses" (e.g. Lund Pointe deducts
  // reserves separately), which causes the formula to overstate NOI vs the OM value.
  // Back-calculating expenses from (EGI - extractedNOI) ensures the scenario model
  // produces exactly the NOI the document reported.
  const shouldTrustExtractedNOI = extractedNOI > 0
  const operatingExpenses = shouldTrustExtractedNOI
    ? Math.max(effectiveGrossIncome - extractedNOI, 0)
    : extractedExpenses || Math.max(effectiveGrossIncome - (grossRentalIncome + otherIncome) * 0.45, 0)
  const loanAmount = purchasePrice * 0.7
  const managementPct = SCENARIOS.base.managementPct
  const managementExpense = effectiveGrossIncome * managementPct
  const remainingOperatingExpenses = Math.max(operatingExpenses - managementExpense, 0)

  // Extract insurance from deal documents first; fall back to proportional allocation.
  const extractedInsurance = positiveNumber(expenses.operatingExpenses?.insurance)
  const rawInsuranceAlloc = Math.round(remainingOperatingExpenses * 0.12)
  const insuranceValue = extractedInsurance
    || rawInsuranceAlloc
    || Math.round(purchasePrice * 0.0025)
  const insuranceIsEstimated = !extractedInsurance

  // Normalize non-insurance buckets to fill exactly (remainingOpExp − insurance).
  // Without this, when extracted insurance ≠ default 12%, the buckets overshoot
  // remainingOpExp and calculate(scenario) produces a different NOI than the
  // document-extracted value — causing the 3-place NOI discrepancy.
  // Weights: propertyTax 32%, utilities 14%, maintenance 24%, reserves 18% = 88%
  const nonInsurancePool = Math.max(remainingOperatingExpenses - insuranceValue, 0)
  const expenseBuckets = {
    propertyTax:        Math.round(nonInsurancePool * (0.32 / 0.88)),
    insurance:          insuranceValue,
    insuranceIsEstimated,
    utilities:          Math.round(nonInsurancePool * (0.14 / 0.88)),
    maintenance:        Math.round(nonInsurancePool * (0.24 / 0.88)),
    reserves:           Math.round(nonInsurancePool * (0.18 / 0.88)),
  }

  const base = {
    ...SCENARIOS.base,
    ...expenseBuckets,
    grossRentalIncome,
    otherIncome,
    vacancyRate,
    purchasePrice,
    loanAmount,
    managementPct,
    ...(dealState?._smartFillOverride || {}),
  }

  return {
    base,
    best: {
      ...base,
      label: 'Best Case',
      color: '#1d4ed8',
      grossRentalIncome: Math.round(grossRentalIncome * 1.08),
      otherIncome: Math.round(otherIncome * 1.15),
      vacancyRate: Math.max(0.03, vacancyRate - 0.02),
      insurance: Math.round(expenseBuckets.insurance * 0.95),
      utilities: Math.round(expenseBuckets.utilities * 0.95),
      maintenance: Math.round(expenseBuckets.maintenance * 0.9),
      managementPct: 0.05,
      interestRate: 0.06,
      exitCapRate: 0.05,
      annualRentGrowth: 0.04,
      annualExpenseGrowth: 0.02,
    },
    worst: {
      ...base,
      label: 'Worst Case',
      color: '#1e3a8a',
      grossRentalIncome: Math.round(grossRentalIncome * 0.92),
      otherIncome: Math.round(otherIncome * 0.85),
      vacancyRate: Math.min(0.18, vacancyRate + 0.05),
      propertyTax: Math.round(expenseBuckets.propertyTax * 1.06),
      insurance: Math.round(expenseBuckets.insurance * 1.16),
      utilities: Math.round(expenseBuckets.utilities * 1.14),
      maintenance: Math.round(expenseBuckets.maintenance * 1.18),
      managementPct: 0.07,
      reserves: Math.round(expenseBuckets.reserves * 1.25),
      interestRate: 0.072,
      exitCapRate: 0.065,
      annualRentGrowth: 0.015,
      annualExpenseGrowth: 0.035,
    },
  }
}

// ─── Core Calculations ─────────────────────────────────────────────────────
function calculate(s) {
  const income = calculateSdaIncomeAndNoi({
    grossPotentialRentAnnual: s.grossRentalIncome,
    otherIncomeAnnual: s.otherIncome,
    vacancyRate: s.vacancyRate,
  })
  const egi = income.effectiveGrossIncome
  const mgmt = egi * s.managementPct
  const totalExpenses =
    s.propertyTax + s.insurance + s.utilities +
    s.maintenance + mgmt + s.reserves
  const rawNoi = egi - totalExpenses
  const noi = toFiniteNumber(rawNoi)
  const capRate = s.purchasePrice > 0 ? noi / s.purchasePrice : 0
  const equity = Math.max(s.purchasePrice - s.loanAmount, 0)

  // Annual debt service (mortgage constant)
  const monthlyRate = s.interestRate / 12
  const n = s.amortization * 12
  const monthlyPayment = s.loanAmount > 0 && monthlyRate > 0 && n > 0
    ? s.loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, n)) /
      (Math.pow(1 + monthlyRate, n) - 1)
    : 0
  const annualDebtService = monthlyPayment * 12

  const dscr = annualDebtService > 0 ? toFiniteNumber(noi / annualDebtService) : 0
  const cashFlowBeforeTax = toFiniteNumber(noi - annualDebtService)
  const cashOnCash = equity > 0 ? cashFlowBeforeTax / equity : 0

  // IRR — 5-year hold, simple approximation
  const cashFlows = [-equity]
  let currentNOI = noi
  let currentLoanBalance = s.loanAmount
  for (let yr = 1; yr <= s.holdYears; yr++) {
    // Principal paydown (simplified)
    const principalPaid = annualDebtService - currentLoanBalance * s.interestRate
    currentLoanBalance -= principalPaid
    const cf = toFiniteNumber(currentNOI - annualDebtService)
    if (yr < s.holdYears) {
      cashFlows.push(cf)
      currentNOI *= (1 + s.annualRentGrowth)
    } else {
      // Exit: sell at exit cap rate on year-5 NOI
      const exitNOI = currentNOI
      const salePrice = exitNOI / s.exitCapRate
      const saleProceeds = toFiniteNumber(salePrice - currentLoanBalance)
      cashFlows.push(cf + saleProceeds)
    }
    currentNOI *= (1 + s.annualRentGrowth - s.annualExpenseGrowth * 0.5)
  }
  const irr = toFiniteNumber(calcIRR(cashFlows))

  // 5-year cash flow table
  const table = []
  let noi_yr = noi
  let bal = s.loanAmount
  for (let yr = 1; yr <= 5; yr++) {
    const interest = bal * s.interestRate
    const principal = annualDebtService - interest
    bal -= principal
    const cf = toFiniteNumber(noi_yr - annualDebtService)
    table.push({
      year: yr,
      noi: Math.round(toFiniteNumber(noi_yr)),
      debtService: Math.round(annualDebtService),
      cashFlow: Math.round(cf),
      balance: Math.round(excelMaxZero(bal)),
    })
    noi_yr *= (1 + s.annualRentGrowth)
  }

  // ── Monthly / Annual cost breakdown ─────────────────────────────────────
  const annualInsurance = s.insurance
  const monthlyInsurance = Math.round(s.insurance / 12)
  const annualPropertyTax = s.propertyTax
  const monthlyPropertyTax = Math.round(s.propertyTax / 12)
  const annualPropertyOpEx = s.utilities + s.maintenance + mgmt + s.reserves
  const monthlyPropertyOpEx = Math.round(annualPropertyOpEx / 12)
  const monthlyDebtService = Math.round(monthlyPayment)
  const totalAnnualOwnershipCost = annualDebtService + totalExpenses
  const totalMonthlyOwnershipCost = Math.round(totalAnnualOwnershipCost / 12)

  // ── Break-even occupancy ─────────────────────────────────────────────────
  // The occupancy rate at which EGI exactly covers all expenses + debt service
  const grossPotentialRevenue = s.grossRentalIncome + s.otherIncome
  const breakEvenOccupancy = grossPotentialRevenue > 0
    ? Math.min(1, (totalExpenses + annualDebtService) / grossPotentialRevenue)
    : 0

  return {
    egi: Math.round(egi),
    mgmt: Math.round(mgmt),
    totalExpenses: Math.round(totalExpenses),
    noi: Math.round(noi),
    capRate,
    dscr,
    cashFlowBeforeTax: Math.round(cashFlowBeforeTax),
    cashOnCash,
    annualDebtService: Math.round(annualDebtService),
    equity: Math.round(equity),
    irr,
    table,
    // Insurance & expense detail
    annualInsurance,
    monthlyInsurance,
    insuranceIsEstimated: s.insuranceIsEstimated || false,
    annualPropertyTax,
    monthlyPropertyTax,
    annualPropertyOpEx: Math.round(annualPropertyOpEx),
    monthlyPropertyOpEx,
    monthlyDebtService,
    totalAnnualOwnershipCost: Math.round(totalAnnualOwnershipCost),
    totalMonthlyOwnershipCost,
    breakEvenOccupancy,
  }
}

function calcIRR(cashFlows) {
  const hasInvestment = cashFlows.some(cf => cf < 0)
  const hasReturn = cashFlows.some(cf => cf > 0)
  if (!hasInvestment || !hasReturn) return 0

  let rate = 0.1
  for (let i = 0; i < 200; i++) {
    let npv = 0, dnpv = 0
    cashFlows.forEach((cf, t) => {
      npv += cf / Math.pow(1 + rate, t)
      dnpv -= (t * cf) / Math.pow(1 + rate, t + 1)
    })
    if (!Number.isFinite(npv) || !Number.isFinite(dnpv) || dnpv === 0) return 0
    const newRate = rate - npv / dnpv
    if (!Number.isFinite(newRate)) return 0
    if (Math.abs(newRate - rate) < 1e-7) return newRate
    rate = newRate
  }
  return Number.isFinite(rate) ? rate : 0
}

// ─── Store ─────────────────────────────────────────────────────────────────
export const useUnderwritingStore = create((set, get) => ({
  activeScenario: 'base',
  scenarios: SCENARIOS,
  isCompleted: false,
  activeTab: 'compare',         // 'compare' | 'metrics' | 'cashflow' | 'assumptions'

  setActiveScenario: (s) => set({ activeScenario: s }),
  setActiveTab: (t) => set({ activeTab: t }),
  markCompleted: () => set({ isCompleted: true }),
  setScenariosFromDealState: (dealState) => set({
    scenarios: buildScenariosFromDealState(dealState),
    isCompleted: false,
  }),

  // Apply a manually-supplied interest rate across all three scenarios.
  // Best case gets -0.5%, worst case gets +0.75% spread around the rate.
  // (Was previously driven by FRED — that integration has been removed.)
  applyLiveInterestRate: (liveRate) => set((state) => ({
    scenarios: {
      ...state.scenarios,
      base:  { ...state.scenarios.base,  interestRate: liveRate },
      best:  { ...state.scenarios.best,  interestRate: Math.max(0.04, liveRate - 0.005) },
      worst: { ...state.scenarios.worst, interestRate: liveRate + 0.0075 },
    },
  })),

  getResults: () => {
    const { activeScenario, scenarios } = get()
    return calculate(scenarios[activeScenario])
  },

  getAllResults: () => {
    const { scenarios } = get()
    return {
      base: calculate(scenarios.base),
      best: calculate(scenarios.best),
      worst: calculate(scenarios.worst),
    }
  },

  // Preview what metrics look like at a renegotiated price (base scenario, price + LTV adjusted).
  calculateWithPrice: (newPrice) => {
    const { scenarios } = get()
    return calculate({
      ...scenarios.base,
      purchasePrice: newPrice,
      loanAmount: Math.round(newPrice * 0.70),
    })
  },

  // Apply a renegotiated purchase price across all three scenarios (70 % LTV).
  updatePurchasePrice: (newPrice) => set((state) => ({
    scenarios: {
      base:  { ...state.scenarios.base,  purchasePrice: newPrice, loanAmount: Math.round(newPrice * 0.70) },
      best:  { ...state.scenarios.best,  purchasePrice: newPrice, loanAmount: Math.round(newPrice * 0.70) },
      worst: { ...state.scenarios.worst, purchasePrice: newPrice, loanAmount: Math.round(newPrice * 0.70) },
    },
  })),
}))
